import { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/tasks/ConfirmDialog";

const snap = (x) => JSON.stringify({
  title: (x.title || "").trim(), summary: (x.summary || "").trim(),
  description: (x.description || "").trim(), priority: x.priority || "normal", owner_id: x.owner_id || "",
});

// Create/Edit Topic drawer. Reuses the canonical drawer shell + unsaved-changes
// guard. New topics are always created with status = Idea (no workflow controls).
export function TopicDrawer({ topicId, canEditAll, onClose, onSaved }) {
  const isCreate = !topicId;
  const [f, setF] = useState({ title: "", summary: "", description: "", priority: "normal", owner_id: "" });
  const [orig, setOrig] = useState(snap({}));
  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [members, setMembers] = useState([]);
  const [confirmClose, setConfirmClose] = useState(false);
  const [confirmRev, setConfirmRev] = useState(false);
  const [status, setStatus] = useState("");
  const loadedRef = useRef({ title: "", summary: "", description: "" });

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (canEditAll) apiFetch("/members").then((m) => setMembers((m || []).filter((x) => x.status === "Active"))).catch(() => {});
    if (isCreate) { setOrig(snap({})); return; }
    setLoading(true);
    apiFetch(`/topics/${topicId}`).then((t) => {
      const next = { title: t.title, summary: t.summary || "", description: t.description || "", priority: t.priority || "normal", owner_id: t.owner_id || "" };
      setF(next); setOrig(snap(next)); setStatus(t.status || "");
      loadedRef.current = { title: t.title || "", summary: t.summary || "", description: t.description || "" };
    }).catch((e) => toast.error("Could not load topic", { description: e.message })).finally(() => setLoading(false));
  }, [topicId, isCreate, canEditAll]);

  useEffect(() => () => { document.body.style.pointerEvents = ""; document.body.style.overflow = ""; document.body.removeAttribute("data-scroll-locked"); }, []);

  const dirty = snap(f) !== orig;
  const attemptClose = useCallback(() => { if (dirty) setConfirmClose(true); else onClose(); }, [dirty, onClose]);
  useEffect(() => {
    const k = (e) => { if (e.key !== "Escape" || confirmClose) return; attemptClose(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [attemptClose, confirmClose]);

  const save = async (confirmRevision = false) => {
    if (!f.title.trim()) return toast.error("Please give the topic a title.");
    const L = loadedRef.current;
    const sensitiveChanged = !isCreate && (f.title.trim() !== (L.title || "").trim()
      || f.summary.trim() !== (L.summary || "").trim() || f.description.trim() !== (L.description || "").trim());
    if (status === "approved" && sensitiveChanged && confirmRevision !== true) { setConfirmRev(true); return; }
    setSaving(true);
    try {
      const body = { title: f.title.trim(), summary: f.summary.trim(), description: f.description.trim(), priority: f.priority };
      if (canEditAll && f.owner_id) body.owner_id = f.owner_id;
      if (isCreate) { const t = await apiFetch("/topics", { method: "POST", body: JSON.stringify(body) }); toast.success("Topic created"); onSaved?.(t.id); }
      else {
        const q = confirmRevision === true ? "?confirm_revision=true" : "";
        const t = await apiFetch(`/topics/${topicId}${q}`, { method: "PATCH", body: JSON.stringify(body) });
        toast.success(confirmRevision ? "Topic revised — review required again" : "Topic updated"); onSaved?.(t.id);
      }
    } catch (e) { toast.error("Could not save topic", { description: e.message }); } finally { setSaving(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-drawer" data-testid="topic-drawer">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px] animate-in fade-in duration-200" onClick={attemptClose} />
      <aside className="absolute inset-y-0 right-0 flex w-full flex-col border-l border-border bg-card shadow-lift animate-in slide-in-from-right duration-300 sm:w-[560px] lg:w-[600px] max-sm:inset-0">
        <header className="flex h-16 shrink-0 items-center justify-between border-b border-border px-5">
          <h2 className="font-heading text-lg font-semibold">{isCreate ? "New Topic" : "Edit Topic"}</h2>
          <button onClick={attemptClose} data-testid="topic-drawer-close" aria-label="Close" className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted"><X className="h-5 w-5" /></button>
        </header>

        {loading ? (
          <div className="flex flex-1 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-accent" /></div>
        ) : (
          <div className="flex-1 space-y-5 overflow-y-auto scrollbar-thin p-6">
            <div>
              <Label htmlFor="topic-title" className="text-xs">Title</Label>
              <Input id="topic-title" value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="What idea should Nirvana explore?" maxLength={300} autoFocus data-testid="topic-title-input" />
            </div>
            <div>
              <Label htmlFor="topic-summary" className="text-xs">Summary <span className="text-muted-foreground/60">(optional)</span></Label>
              <Input id="topic-summary" value={f.summary} onChange={(e) => set("summary", e.target.value)} placeholder="A one-line description" maxLength={500} data-testid="topic-summary-input" />
            </div>
            <div>
              <Label htmlFor="topic-desc" className="text-xs">Description <span className="text-muted-foreground/60">(optional)</span></Label>
              <Textarea id="topic-desc" value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="Add context, angles, references…" rows={6} data-testid="topic-description-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Priority</Label>
                <Select value={f.priority} onValueChange={(v) => set("priority", v)}>
                  <SelectTrigger data-testid="topic-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {canEditAll && (
                <div>
                  <Label className="text-xs">Owner</Label>
                  <Select value={f.owner_id || "me"} onValueChange={(v) => set("owner_id", v === "me" ? "" : v)}>
                    <SelectTrigger data-testid="topic-owner"><SelectValue placeholder="Myself" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="me">Myself</SelectItem>
                      {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.full_name || m.name || m.login_email}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
        )}

        <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-border p-4">
          <Button variant="outline" onClick={attemptClose} data-testid="topic-cancel">Cancel</Button>
          <Button onClick={() => save()} disabled={saving || !f.title.trim()} data-testid="topic-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isCreate ? "Create Topic" : "Save Changes"}</Button>
        </footer>
      </aside>

      <ConfirmDialog open={confirmClose} onOpenChange={(o) => !o && setConfirmClose(false)} testid="topic-discard-dialog" destructive
        title="Discard changes?" description="You have unsaved changes. Close without saving?" confirmLabel="Discard" onConfirm={() => { setConfirmClose(false); onClose(); }} />
      <ConfirmDialog open={confirmRev} onOpenChange={(o) => !o && setConfirmRev(false)} testid="topic-revision-dialog"
        title="Revise this approved topic?" description="This topic has already been approved. Changing its core content will require a new review. The previous approval is kept as history."
        confirmLabel="Revise Topic" onConfirm={() => { setConfirmRev(false); save(true); }} destructive={false} />
    </div>,
    document.body
  );
}
